import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/landing/Footer";

const EFFECTIVE_DATE = "December 1, 2025";

export const Terms = () => {
  return (
    <div data-testid="terms-page" className="min-h-screen bg-white text-[#0A0A0A]">
      <header className="border-b border-[#E5E5E5] bg-white/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="terms-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A0A0A] hover:text-[#0052FF] transition-colors"
          >
            <ArrowLeft size={16} />
            Back to OperatorAI
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#0A0A0A] text-white font-extrabold text-sm tracking-tight">
              O
            </span>
            <span className="font-extrabold tracking-tight text-lg">
              OperatorAI
            </span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0052FF] mb-5">
          Legal
        </div>
        <h1
          data-testid="terms-headline"
          className="text-4xl sm:text-5xl font-extrabold tracking-tighter leading-tight text-[#0A0A0A]"
        >
          Terms of Use
        </h1>
        <p className="mt-4 text-sm text-[#525252]">
          Effective Date: {EFFECTIVE_DATE}
        </p>

        <div className="mt-12 space-y-10">
          <Section title="1. Acceptance of Terms">
            <p>
              By accessing or using{" "}
              <a className="text-[#0052FF] hover:underline" href="https://operatoraihq.com">
                operatoraihq.com
              </a>{" "}
              (the &ldquo;Site&rdquo;) and any content, newsletters, guides,
              or resources we provide (collectively, the
              &ldquo;Services&rdquo;), you agree to be bound by these Terms
              of Use. If you do not agree, please do not use the Services.
            </p>
          </Section>

          <Section title="2. About OperatorAI">
            <p>
              OperatorAI publishes educational content about practical AI
              workflows, tools, and automation systems for operators,
              consultants, agencies, and small businesses. We are not a
              consulting firm, law firm, accounting firm, or financial
              advisor.
            </p>
          </Section>

          <Section title="3. Educational Content Only">
            <p>
              All content on the Site &mdash; including newsletters, guides,
              videos, and posts &mdash; is provided for general
              informational and educational purposes only. It is{" "}
              <strong>not</strong> professional advice (legal, financial,
              tax, business, or otherwise).
            </p>
            <p>
              You are solely responsible for evaluating whether any
              workflow, tool, or recommendation is appropriate for your
              business. Always perform your own due diligence before
              implementing third-party tools or making business decisions.
            </p>
          </Section>

          <Section title="4. Affiliate Links & Sponsorships">
            <p>
              Some content may contain affiliate links or sponsored
              recommendations. If you click an affiliate link and make a
              purchase, we may earn a commission at no additional cost to
              you. We only recommend tools we believe provide real value.
            </p>
            <p>
              Sponsored content will be disclosed in accordance with
              applicable advertising guidelines.
            </p>
          </Section>

          <Section title="5. Third-Party Tools & Links">
            <p>
              The Site may reference or link to third-party products,
              services, or websites. We do not control and are not
              responsible for the content, terms, privacy practices, or
              availability of any third-party platform.
            </p>
          </Section>

          <Section title="6. Intellectual Property">
            <p>
              All content on the Site &mdash; including text, graphics,
              logos, guides, and code &mdash; is owned by OperatorAI or its
              licensors and is protected by applicable intellectual property
              laws. You may share short excerpts with proper attribution and
              a link back to the original page. Republishing, copying, or
              redistributing full content without written permission is
              prohibited.
            </p>
          </Section>

          <Section title="7. Acceptable Use">
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Use the Services in any way that violates applicable laws or
                regulations.
              </li>
              <li>
                Attempt to interfere with, disrupt, or gain unauthorized
                access to the Site or its underlying infrastructure.
              </li>
              <li>
                Scrape, harvest, or otherwise extract content from the Site
                for commercial use without permission.
              </li>
              <li>
                Submit false, misleading, or fraudulent information through
                our forms.
              </li>
            </ul>
          </Section>

          <Section title="8. Newsletter & Communications">
            <p>
              By subscribing, you consent to receive emails from OperatorAI,
              including the Free AI Workflow Guide, weekly content, and
              occasional updates. You can unsubscribe at any time using the
              link in any email or by contacting{" "}
              <a className="text-[#0052FF] hover:underline" href="mailto:hello@operatoraihq.com">
                hello@operatoraihq.com
              </a>
              .
            </p>
          </Section>

          <Section title="9. Disclaimers">
            <p>
              The Services are provided on an &ldquo;as is&rdquo; and
              &ldquo;as available&rdquo; basis, without warranties of any
              kind, express or implied. We do not warrant that the Services
              will be uninterrupted, error-free, or that any content is
              accurate, complete, or current.
            </p>
            <p>
              Results from implementing any AI workflow, tool, or
              recommendation depend on your specific business, context, and
              execution. We make no guarantees of outcomes, revenue, or time
              savings.
            </p>
          </Section>

          <Section title="10. Limitation of Liability">
            <p>
              To the maximum extent permitted by law, OperatorAI and its
              affiliates, employees, and partners shall not be liable for
              any indirect, incidental, special, consequential, or punitive
              damages, or any loss of profits, revenue, data, or business
              opportunity, arising out of or in connection with your use of
              the Services.
            </p>
          </Section>

          <Section title="11. Indemnification">
            <p>
              You agree to indemnify and hold harmless OperatorAI from any
              claims, damages, liabilities, and expenses (including
              reasonable attorneys&apos; fees) arising from your use of the
              Services or your violation of these Terms.
            </p>
          </Section>

          <Section title="12. Changes to These Terms">
            <p>
              We may update these Terms from time to time. The effective
              date at the top of this page reflects the most recent
              revision. Continued use of the Services after changes
              constitutes acceptance of the updated Terms.
            </p>
          </Section>

          <Section title="13. Governing Law">
            <p>
              These Terms are governed by the laws of the jurisdiction in
              which OperatorAI is established, without regard to its
              conflict-of-laws principles. Any disputes shall be resolved in
              the courts of that jurisdiction.
            </p>
          </Section>

          <Section title="14. Contact">
            <p>
              Questions about these Terms? Email{" "}
              <a className="text-[#0052FF] hover:underline" href="mailto:hello@operatoraihq.com">
                hello@operatoraihq.com
              </a>
              .
            </p>
          </Section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

const Section = ({ title, children }) => (
  <section>
    <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#0A0A0A]">
      {title}
    </h2>
    <div className="mt-4 space-y-4 text-base leading-relaxed text-[#374151]">
      {children}
    </div>
  </section>
);

export default Terms;
